import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './Docs.css';

export default function WebRTCPresence() {
  return (
    <div className="docs-lite">
      <Helmet>
        <link rel="canonical" href="https://www.joegalaxy.net/docs/webrtc-presence" />
      </Helmet>

      <header className="docs-lite__header">
        <h1>WebRTC Presence: Readiness Before Calls</h1>
      </header>

      <main className="docs-lite__content">
        <p>
          WebRTC enables real-time audio and video, but{' '}
          <strong>media alone does not create successful interaction</strong>.
          <br />
          Most A/V systems fail because calls are initiated without context, readiness, or intent.
        </p>

        <p>
          This document explains why <strong>presence must exist before WebRTC</strong>, and how
          lifecycle-aware presence turns blind calls into coordinated interactions.
        </p>

        <hr />

        <section>
          <h2>The problem with blind calls</h2>
          <p>Traditional WebRTC flows look like this:</p>
          <ol>
            <li>User clicks “Call”</li>
            <li>Media negotiation starts</li>
            <li>The other side may or may not answer</li>
          </ol>
          <p>This model assumes:</p>
          <ul>
            <li>the recipient is online</li>
            <li>the recipient is available</li>
            <li>the timing is appropriate</li>
          </ul>
          <p>In real systems, these assumptions are often false.</p>
          <p>The result:</p>
          <ul>
            <li>missed calls</li>
            <li>repeated retries</li>
            <li>notification spam</li>
            <li>degraded trust</li>
          </ul>
          <p>Media quality does not fix coordination failures.</p>
        </section>

        <hr />

        <section>
          <h2>Presence before media</h2>
          <p>Presence answers questions WebRTC cannot:</p>
          <ul>
            <li>Is anyone here?</li>
            <li>Are they active or idle?</li>
            <li>Are they available to talk?</li>
            <li>Is the room warming up or empty?</li>
          </ul>
          <p>
            When presence exists <strong>before</strong> media:
          </p>
          <ul>
            <li>calls stop being blind</li>
            <li>interactions start when readiness exists</li>
            <li>media becomes an outcome, not a gamble</li>
          </ul>
          <p>
            Presence is not an enhancement to WebRTC.
            <br />
            It is a prerequisite.
          </p>
        </section>

        <hr />

        <section>
          <h2>Readiness vs availability</h2>
          <p>
            Availability is binary and misleading.
            <br />
            Readiness is contextual and time-based.
          </p>
          <p>A presence-native system can express readiness as:</p>
          <ul>
            <li>active presence</li>
            <li>recent interaction</li>
            <li>declared intent</li>
            <li>room density</li>
            <li>lifecycle state</li>
          </ul>
          <p>Calls should start when readiness crosses a threshold, not when a button is clicked.</p>
        </section>

        <hr />

        <section>
          <h2>Rooms that exist before calls</h2>
          <p>
            In a presence-native model, rooms are <strong>persistent coordination spaces</strong>,
            not ephemeral calls.
          </p>
          <p>A room can exist:</p>
          <ul>
            <li>before audio/video starts</li>
            <li>while participants gather</li>
            <li>after media ends</li>
          </ul>
          <p>Presence within a room shows:</p>
          <ul>
            <li>who is present</li>
            <li>who is arriving</li>
            <li>who is idle</li>
            <li>momentum over time</li>
          </ul>
          <p>Media attaches only when it adds value.</p>
        </section>

        <hr />

        <section>
          <h2>Intent-aware call initiation</h2>
          <p>If a target is offline or idle, calling immediately is often wrong.</p>
          <p>Instead of ringing, a system can:</p>
          <ul>
            <li>record intent to connect</li>
            <li>notify the target asynchronously</li>
            <li>allow them to join when ready</li>
          </ul>
          <p>
            This transforms calling into <strong>coordination</strong>, not interruption.
          </p>
          <p>Presence makes intent observable.</p>
        </section>

        <hr />

        <section>
          <h2>Guest participation</h2>
          <p>Presence-first WebRTC enables guest access without commitment.</p>
          <p>Guests can:</p>
          <ul>
            <li>observe room presence</li>
            <li>join silently</li>
            <li>become active later</li>
            <li>authenticate only when needed</li>
          </ul>
          <p>This lowers friction and improves adoption, especially for:</p>
          <ul>
            <li>communities</li>
            <li>events</li>
            <li>live collaboration</li>
            <li>social platforms</li>
          </ul>
          <p>Identity layers in after presence.</p>
        </section>

        <hr />

        <section>
          <h2>Observability and coordination intelligence</h2>
          <p>
            When presence and WebRTC are combined correctly, systems gain insight beyond media
            metrics.
          </p>
          <p>Examples:</p>
          <ul>
            <li>room readiness curves</li>
            <li>join and drop-off patterns</li>
            <li>attention decay</li>
            <li>density thresholds for starting or ending interaction</li>
          </ul>
          <p>
            This is not call analytics.
            <br />
            It is <strong>coordination intelligence</strong>.
          </p>
        </section>

        <hr />

        <section>
          <h2>A minimal conceptual flow</h2>
          <pre>
            <code>{`presence established
   ↓
readiness observed
   ↓
intent signaled (if needed)
   ↓
media attached
   ↓
interaction
   ↓
presence persists after media`}</code>
          </pre>
          <p>This flow avoids blind calls and reduces coordination friction.</p>
        </section>

        <hr />

        <section>
          <h2>Summary</h2>
          <p>
            WebRTC solves media transport.
            <br />
            Presence solves coordination.
          </p>
          <p>When combined:</p>
          <ul>
            <li>
              calls start at the right time
              <br />
            </li>
            <li>
              rooms feel alive instead of empty
              <br />
            </li>
            <li>
              users trust the system
              <br />
            </li>
            <li>
              interaction becomes predictable
              <br />
            </li>
          </ul>
          <p>
            <strong>Presence-native WebRTC is not about better calls.</strong>
            <br />
            It is about better timing.
          </p>
        </section>

        <hr />

        <section>
          <h2>Related docs</h2>
          <ul>
            <li>
              <Link to="/docs/index.md">Docs index</Link>
            </li>
            <li>
              <Link to="/docs/presence-online-offline.md">Online vs Offline Presence</Link>
            </li>
            <li>
              <Link to="/docs/whos-online-api.md">Who’s Online API</Link>
            </li>
            <li>
              <Link to="/docs/presence-socket-io.md">Presence with Socket.IO</Link>
            </li>
          </ul>
        </section>

        <p className="docs-lite__source">
          —
          <br />
          See this model running live:
          <br />
          <a href="https://www.joegalaxy.net/presence-demo">
            https://www.joegalaxy.net/presence-demo
          </a>
          <br />
          <a href="https://github.com/thejoegalaxy/presence-engine/blob/main/docs/webrtc-presence.md">
            View source on GitHub
          </a>
        </p>
      </main>
    </div>
  );
}
